// packages
import { json } from 'd3-fetch';
import { select } from 'd3-selection';
import { geoIdentity, geoPath } from 'd3-geo';

// components
import Tooltip from './TooltipClosures.svelte';

// utils
import { getGeoJsonFromTopoJson } from '../../../utils/prepare-topojson';
import { loadJsonScript } from '../../../utils/load-json-script';
import { relocateTooltip } from '../../../utils/relocate-tooltip';

// data
const closures = loadJsonScript('closures-data');
// Parent elements

const fipsLookup = closures.reduce((obj, item) => {
  obj[item.fips] = item;
  return obj;
}, {});

let topoData;

async function getTopoJson() {
  if (!topoData) {
    topoData = {};
    const california = await json(window.TOPOJSON.california_new);
    topoData.counties = getGeoJsonFromTopoJson(california, 'counties', {
      innerlines: true,
    });
    topoData.regions = getGeoJsonFromTopoJson(california, 'regions');
    topoData.cities = getGeoJsonFromTopoJson(california, 'cities');
  }
  return topoData;
}

// Mapping projections
const projection = geoIdentity().reflectY(true);
const path = geoPath(projection);

// Tooltip
const tooltipElement = document.getElementById('tooltip__california-closures');
const tooltip = new Tooltip({ target: tooltipElement });
const activateTooltip = () => tooltipElement.classList.remove('invisible');
const deactivateTooltip = () => tooltipElement.classList.add('invisible');
let activeFips = null;

// Colors
const paletteSet = {
  tri_color: ['#e2be41', '#a3b6c8', '#adc839'],
  opening_scale: ['#e6b735', '#e6b735', '#d97641', '#c43d53', '#802f67'],
};

const PENDING_STAY_HOME_COLOR = '#4089B2';
const ACTIVE_STAY_HOME_COLOR = '#1f2870';

export async function render(variable, id, description, scale_type) {
  const { counties, cities } = await getTopoJson();

  const container = select('#' + id);

  const width = 620;
  const height = 620;

  projection.fitSize([width, height], counties.collection);

  const svg = container.append('svg').attr('viewBox', `0 0 640 640`);

  // Colors
  const palette = paletteSet[scale_type];

  const g = svg.append('g').attr('class', 'layer-container');

  const countiesGroup = g.append('g');

  countiesGroup
    .selectAll('path')
    .data(counties.collection.features)
    .join('path')
    .attr('class', 'county')
    .attr('data-fips', (d) => d.properties.fips)
    .attr('data-name', (d) => d.properties.name)
    .attr('fill', (d) => {
      const data = fipsLookup[d.properties.fips];
      if (data) {
        if (data.stay_home_order === 'pending') {
          return PENDING_STAY_HOME_COLOR;
        }
        if (data.stay_home_order === 'voluntary') {
          return ACTIVE_STAY_HOME_COLOR;
        }
        if (data.stay_home_order === 'yes') {
          return ACTIVE_STAY_HOME_COLOR;
        }
        const dataColor = data[variable];
        if (dataColor != null) {
          return palette[dataColor];
        } else {
          return '#eaeaea';
        }
      }
    })
    .attr('d', path)
    .on('mouseenter', function () {
      this.classList.add('active-path');
      select(this).raise();
      activateTooltip();
    })
    .on('mousemove', function (event, d) {
      if (activeFips == d.properties.fips) {
        return false;
      }
      const data = fipsLookup[d.properties.fips];
      // let openings = '';
      // let limiteds = '';
      // for (var i = 0; i < data['open_list'].length; i++) {
      //   openings += '<li>' + data['open_list'][i] + '</li>';
      // }
      //
      // for (var j = 0; j < data['limited_list'].length; j++) {
      //   limiteds += '<li>' + data['limited_list'][j] + '</li>';
      // }

      let tier_restriction;
      let tier_text;
      let tier_num;

      if (data.tier == 1) {
        tier_text = 'Most nonessential indoor business operations were closed.';
        tier_num = 1;
        tier_restriction = 'Widespread';
      } else if (data.tier == 2) {
        tier_text = 'Some nonessential indoor business operations were closed.';
        tier_num = 2;
        tier_restriction = 'Substantial';
      } else if (data.tier == 3) {
        tier_text = 'Some business operations could open with modifications.';
        tier_num = 3;
        tier_restriction = 'Moderate';
      } else if (data.tier == 4) {
        tier_text = 'Most business operations could open with modifications';
        tier_num = 4;
        tier_restriction = 'Minimal';
      }
      activateTooltip();
      let context;
      if (
        data &&
        description == 'overall_status' &&
        data[description] != null
      ) {
        context = {
          id: 'large_map_tooltip',
          name: data.county,
          tooltip_text: tier_text,
          tier_level: tier_num,
          tier_restriction: tier_restriction,
          region: data.region,
        };
      } else if (
        data &&
        description != 'openness_score' &&
        data[description] != null
      ) {
        context = {
          id: 'small_map_tooltip',
          name: data.county,
          tooltip_text: data[description],
        };
      } else {
        context = {
          id: 'small_map_tooltip',
          name: d.properties.name,
          tooltip_text: '',
        };
      }
      tooltip.$set(context);
      const e = event;
      relocateTooltip(tooltipElement, e.clientX, e.clientY);
      activeFips = d.properties.fips;
    })
    .on('mouseleave', function () {
      select(this).classed('active-path', false).lower();
      deactivateTooltip();
    });

  countiesGroup
    .append('path')
    .attr('class', 'county--innerlines')
    .attr('fill', 'none')
    .attr('stroke', '#ffffff')
    .attr('stroke-linejoin', 'round')
    .attr('d', path(counties.innerlines));

  // Draw cities
  const citiesGroup = svg.append('g').attr('class', 'cities');

  const city = citiesGroup
    .selectAll('g')
    .data(cities.collection.features)
    .join('g')
    .attr(
      'transform',
      (d) => `translate(${projection(d.geometry.coordinates)})`
    );

  city.append('circle').attr('class', 'city').attr('r', 4);
  city
    .append('text')
    .attr('class', 'halo')
    .attr('dx', (d) => (d.properties.orientation == 'right' ? '8px' : '-8px'))
    .attr('dy', '6px')
    .attr('text-anchor', (d) =>
      d.properties.orientation == 'right' ? 'start' : 'end'
    )
    .text((d) => d.properties.name);
  city
    .append('text')
    .attr('class', 'text')
    .attr('dx', (d) => (d.properties.orientation == 'right' ? '8px' : '-8px'))
    .attr('dy', '6px')
    .attr('text-anchor', (d) =>
      d.properties.orientation == 'right' ? 'start' : 'end'
    )
    .text((d) => d.properties.name);
}
